import * as CANNON from 'cannon-es';
import {
  PANEL_SIZE,
  BALL_RADIUS,
  WALL_HEIGHT,
  WALL_THICKNESS,
  BALL_MASS,
  PHYSICS_FIXED_TIMESTEP,
  PHYSICS_MAX_SUBSTEPS,
  GRAVITY,
} from '../config.js';
import { computeGravityVector } from './physics/gravity.js';
import { wallSegmentBox } from './wallGeometry.js';

export class PhysicsWorld {
  constructor() {
    this.world = new CANNON.World();
    this.world.gravity.set(0, -GRAVITY, 0);
    this.world.broadphase = new CANNON.NaiveBroadphase();
    this.world.allowSleep = false;

    this.ballMaterial = new CANNON.Material('ball');
    this.wallMaterial = new CANNON.Material('wall');
    this.floorMaterial = new CANNON.Material('floor');
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.ballMaterial, this.floorMaterial, {
        friction: 0.1,
        restitution: 0.1,
      })
    );
    this.world.addContactMaterial(
      new CANNON.ContactMaterial(this.ballMaterial, this.wallMaterial, {
        friction: 0.05,
        restitution: 0.3,
      })
    );

    /** @type {CANNON.Body|null} */
    this.ballBody = null;
    /** @type {CANNON.Body[]} */
    this.wallBodies = [];
    /** @type {CANNON.Body|null} */
    this.floorBody = null;
  }

  buildStaticGeometry(level) {
    this.floorBody = new CANNON.Body({
      mass: 0,
      material: this.floorMaterial,
      shape: new CANNON.Plane(),
    });
    this.floorBody.quaternion.setFromEuler(-Math.PI / 2, 0, 0);
    this.world.addBody(this.floorBody);

    const half = PANEL_SIZE / 2;
    const t = WALL_THICKNESS;
    // 外围 4 堵墙（内侧对齐 0 / PANEL_SIZE，即世界坐标 ±half）
    const outerWalls = [
      { cx: 0, cz: -half - t / 2, sx: PANEL_SIZE + 2 * t, sz: t },
      { cx: 0, cz: half + t / 2, sx: PANEL_SIZE + 2 * t, sz: t },
      { cx: -half - t / 2, cz: 0, sx: t, sz: PANEL_SIZE },
      { cx: half + t / 2, cz: 0, sx: t, sz: PANEL_SIZE },
    ];
    for (const w of outerWalls) this._addWallBody(w.cx, w.cz, w.sx, w.sz);

    // 内部墙：grid (fx,fy)→(tx,ty)；端点各向外延伸 t/2，使 L/T 形接缝处互相重叠补角
    for (const wall of level.walls) {
      const box = wallSegmentBox(wall, PANEL_SIZE, t);
      this._addWallBody(box.cx, box.cz, box.sx, box.sz);
    }
  }

  _addWallBody(cx, cz, sx, sz) {
    const body = new CANNON.Body({
      mass: 0,
      material: this.wallMaterial,
      shape: new CANNON.Box(new CANNON.Vec3(sx / 2, WALL_HEIGHT / 2, sz / 2)),
    });
    body.position.set(cx, WALL_HEIGHT / 2, cz);
    this.world.addBody(body);
    this.wallBodies.push(body);
  }

  spawnBall(gridX, gridY) {
    const half = PANEL_SIZE / 2;
    const wx = gridX - half;
    const wz = half - gridY;
    this.ballBody = new CANNON.Body({
      mass: BALL_MASS,
      material: this.ballMaterial,
      shape: new CANNON.Sphere(BALL_RADIUS),
      linearDamping: 0.03,
      angularDamping: 0.05,
    });
    this.ballBody.position.set(wx, BALL_RADIUS, wz);
    this.world.addBody(this.ballBody);
    return this.ballBody;
  }

  resetBall(gridX, gridY) {
    if (!this.ballBody) return;
    const half = PANEL_SIZE / 2;
    this.ballBody.position.set(gridX - half, BALL_RADIUS, half - gridY);
    this.ballBody.velocity.set(0, 0, 0);
    this.ballBody.angularVelocity.set(0, 0, 0);
    this.ballBody.quaternion.set(0, 0, 0, 1);
  }

  setTilt(tiltX, tiltZ) {
    const { gx, gy, gz } = computeGravityVector(tiltX, tiltZ, GRAVITY);
    this.world.gravity.set(gx, gy, gz);
  }

  step(dt) {
    this.world.step(PHYSICS_FIXED_TIMESTEP, dt, PHYSICS_MAX_SUBSTEPS);
  }

  clear() {
    for (const b of this.wallBodies) this.world.removeBody(b);
    this.wallBodies = [];
    if (this.ballBody) {
      this.world.removeBody(this.ballBody);
      this.ballBody = null;
    }
    if (this.floorBody) {
      this.world.removeBody(this.floorBody);
      this.floorBody = null;
    }
  }
}
